import React from 'react';
import { useLocation } from 'react-router-dom';
import { ShieldOff } from 'lucide-react';
import { useAuth } from '../auth/AuthContext';

export default function ProtectedRoute({ permission, anyOf, children }) {
  const { user, loading, loggingOut, hasPermission } = useAuth();
  const location = useLocation();

  // Treat an in-flight logout the same as loading — otherwise this route
  // can see user=null mid-logout and race its own redirect against the
  // SSO logout redirect (see AuthContext.logout()).
  if (loading || loggingOut) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', flexDirection: 'column', gap: '16px',
      }}>
        <div className="loader" />
        <div style={{ fontFamily: 'var(--serif)', fontStyle: 'italic', color: 'var(--ink-600)' }}>
          One moment…
        </div>
      </div>
    );
  }

  // BFF Redirect: If no user session exists, send them directly to the backend login flow
  if (!user) {
    // We exit the React app and let the backend handle the OAuth handshake
    window.location.href = `/api/auth/login?returnTo=${encodeURIComponent(location.pathname)}`;
    return null;
  }

  // Permission gating
  let allowed = true;
  if (permission) allowed = hasPermission(permission);
  if (anyOf && Array.isArray(anyOf) && anyOf.length) {
    allowed = anyOf.some((p) => hasPermission(p));
  }

  if (!allowed) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '40px',
      }}>
        <div className="panel" style={{ maxWidth: 520, padding: '32px 36px', textAlign: 'center' }}>
          <ShieldOff size={36} color="var(--saffron-deep)" style={{ marginBottom: 12 }} />
          <div className="t-display" style={{ fontSize: 30, marginBottom: 8 }}>
            Insufficient privileges
          </div>
          <div className="rule-ornament" style={{ margin: '12px auto', maxWidth: 200 }} />
          <p style={{ color: 'var(--ink-600)', fontSize: 14, lineHeight: 1.6 }}>
            Your role does not include access to this section. If you believe this is an error,
            please contact a Samvad Mitra administrator.
          </p>
          <div style={{ marginTop: 20, fontSize: 12, color: 'var(--ink-400)' }}>
            Required permission: <code style={{ fontFamily: 'var(--mono)' }}>{permission || (anyOf && anyOf.join(' / '))}</code>
          </div>
        </div>
      </div>
    );
  }

  return children;
}